import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../lib/useAuth'
import { useDrafts } from '../contexts/DraftContext'
import { usePlatforms } from '../contexts/PlatformContext'
import { useSettings } from '../contexts/SettingsContext'
import { platforms } from '../config/platforms'
import { supabase } from '../lib/supabase'
import { getAuthHeaders } from '../lib/authFetch'
import './AccountPage.css'
import { BlueskyConnectModal } from './BlueskyConnectModal'
import { PlatformRow } from './PlatformRow'
import { ViewHeader } from './ViewHeader'

declare global {
  interface Window {
    onSignInSuccess: (data: any) => void;
  }
}

const FarcasterConnectButton = ({ onSignInSuccess }: { onSignInSuccess: (data: any) => void }) => {
  useEffect(() => {
    window.onSignInSuccess = onSignInSuccess;

    const scriptId = 'neynar-siwn-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://neynarxyz.github.io/siwn/raw/1.2.0/index.js';
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      // Optional: Clean up the global callback and script if the component unmounts.
      // delete window.onSignInSuccess;
      // if (script && script.parentNode) {
      //   script.parentNode.removeChild(script);
      // }
    };
  }, [onSignInSuccess]);

  return (
    <div
      className="neynar_signin"
      data-client_id={process.env.REACT_APP_NEYNAR_CLIENT_ID!}
      data-success-callback="onSignInSuccess"
      data-padding="0"
      data-margin="0"
      data-font_size="14px"
      data-font_weight="500"
      data-text="Connect"
      data-color="var(--main-action-color)"
      data-background_color="transparent"
      data-styles='{ "border": "none","justifyContent": "left", "height": "unset" }'
    ></div>
  );
};


export const AccountPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { session, signInWithGoogle, signOut } = useAuth()
  const { enabledPlatforms, togglePlatform, postingMethods, setPostingMethod, platformConnections, setPlatformConnections } = usePlatforms()
  const { settings, updateSettings } = useSettings()
  const { exportDrafts, importDrafts } = useDrafts()
  const [isBetaUser, setIsBetaUser] = useState(false)
  const [showBlueskyModal, setShowBlueskyModal] = useState(false)
  const [disconnecting, setDisconnecting] = useState<string | null>(null)
  const [connecting, setConnecting] = useState<string | null>(null)
  const [hasLoadedConnections, setHasLoadedConnections] = useState(false)
  const [accountNotice, setAccountNotice] = useState<string | null>(null)
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const userId = session?.user?.id
  const hasAccount = !!session?.user
  const hasOauthConnection = useMemo(() => Object.values(platformConnections).some(Boolean), [platformConnections])

  const showNotice = useCallback((message: string) => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
    setAccountNotice(message)
    noticeTimerRef.current = setTimeout(() => setAccountNotice(null), 5000)
  }, [])

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
    }
  }, [])

  const handleFarcasterSignIn = async (data: { fid: string; signer_uuid: string }) => {
    if (!session?.user) return;
    setConnecting('farcaster');
    try {
      const base = process.env.REACT_APP_API_URL;
      const isLocalHost = /^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+)$/.test(window.location.hostname);
      const url = isLocalHost
        ? `/api/auth/farcaster/store-credentials`
        : (base ? `${base.replace(/\/$/, '')}/api/auth/farcaster/store-credentials` : `/api/auth/farcaster/store-credentials`);
      const response = await fetch(url, {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          userId: session.user.id,
          fid: data.fid,
          signer_uuid: data.signer_uuid,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to store Farcaster credentials.');
      }

      await checkPlatformConnections();
    } catch (error) {
      console.error('Farcaster sign-in error:', error);
      alert('Failed to connect Farcaster account.');
    } finally {
      setConnecting(null);
    }
  };
  
  const checkPlatforms = useCallback(async () => {
    if (!userId) return
    try {
      const base = process.env.REACT_APP_API_URL
      const isLocalHost = /^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+)$/.test(window.location.hostname)
      const url = isLocalHost
        ? `/api/user/platforms`
        : (base ? `${base.replace(/\/$/, '')}/api/user/platforms` : `/api/user/platforms`)
      console.debug('[AccountPage] checkPlatforms', { url })
      const response = await fetch(url, { headers: await getAuthHeaders() })
      const contentType = response.headers.get('content-type') || ''
      const data = contentType.includes('application/json') ? await response.json() : { error: await response.text() }
      if (!response.ok) {
        console.error('[AccountPage] checkPlatforms error', { status: response.status, body: data })
        return
      }
    } catch (error) {
      console.error('Failed to check platforms:', error)
    }
  }, [userId])

  const checkBetaStatus = useCallback(async () => {
    if (!userId) return
    let isBeta = false
    try {
      const { data } = await supabase
        .from('beta_users')
        .select('beta_status, requested')
        .eq('user_id', userId)
        .single()
      if (data) {
        isBeta = data.beta_status
      } else {
        // No row exists, insert one with beta_status = false
        await supabase
          .from('beta_users')
          .insert([{ user_id: userId, beta_status: false, requested: false }])
        isBeta = false
      }
    } catch (err) {
      console.error('Failed to check or insert beta status:', err)
    }
    setIsBetaUser(isBeta)
  }, [userId])

  const checkPlatformConnections = useCallback(async () => {
    if (!userId) return;
    try {
      const base = process.env.REACT_APP_API_URL
      const isLocalHost = /^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+)$/.test(window.location.hostname)
      const url = isLocalHost
        ? `/api/user/platforms`
        : (base ? `${base.replace(/\/$/, '')}/api/user/platforms` : `/api/user/platforms`)
      console.debug('[AccountPage] checkPlatformConnections', { url })
      const response = await fetch(url, { headers: await getAuthHeaders() });
      const contentType = response.headers.get('content-type') || ''
      const data = contentType.includes('application/json') ? await response.json() : { error: await response.text() }
      if (!response.ok) {
        console.error('[AccountPage] checkPlatformConnections error', { status: response.status, body: data })
        setHasLoadedConnections(true);
        return
      }
      if (data.platforms) {
        console.debug('[AccountPage] setPlatformConnections <-', data.platforms)
        setPlatformConnections(data.platforms);
      }
      setHasLoadedConnections(true);
    } catch (error) {
      console.error('Failed to check platform connections:', error);
      setHasLoadedConnections(true);
    }
  }, [userId, setPlatformConnections]);

  useEffect(() => {
    if (userId) {
      setHasLoadedConnections(false);
      checkPlatforms();
      checkBetaStatus();
      checkPlatformConnections();
    }
  }, [userId, checkPlatforms, checkBetaStatus, checkPlatformConnections]);

  useEffect(() => {
    const error = searchParams.get('error');
    if (!error) return;

    if (error === 'twitter_auth_failed') {
      showNotice('X/Twitter connection failed.');
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('error');
    nextParams.delete('twitter');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, showNotice]);

  useEffect(() => {
    if (searchParams.get('twitter') !== 'connected' || !hasLoadedConnections) return;

    Object.entries(platformConnections).forEach(([platform, isConnected]) => {
      if (isConnected && postingMethods[platform as keyof typeof postingMethods] === 'intent') {
        setPostingMethod(platform as keyof typeof platforms, 'oauth');
      }
    });

    showNotice('X/Twitter connected.');

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('twitter');
    nextParams.delete('error');
    setSearchParams(nextParams, { replace: true });
  }, [hasLoadedConnections, platformConnections, postingMethods, searchParams, setPostingMethod, setSearchParams, showNotice]);

  const handleConnect = async (platformKey: string) => {
    if (!session?.user) {
      console.error('[AccountPage] No session or user found');
      alert('Please sign in first before connecting platforms.');
      return;
    }
    
    if (!session.user.id) {
      console.error('[AccountPage] User ID is missing from session:', session);
      alert('Authentication error: User ID not found. Please sign out and sign in again.');
      return;
    }
    
    setConnecting(platformKey);
    try {
      if (platformKey === 'twitter') {
        const base = process.env.REACT_APP_API_URL
        const isLocalHost = /^(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+)$/.test(window.location.hostname)
        const twitterAuthUrl = isLocalHost
          ? `/api/twitter/auth`
          : (base ? `${base.replace(/\/$/, '')}/api/twitter/auth` : `/api/twitter/auth`)
        console.debug('[AccountPage] twitter auth URL', { url: twitterAuthUrl, userId: session.user.id })
        const response = await fetch(twitterAuthUrl, { headers: await getAuthHeaders() });
        if (response.status === 403) {
          alert('X/Twitter OAuth is not available for this account right now.');
          setConnecting(null);
          return;
        }
        const { url } = await response.json();
        window.location.href = url;
      } else if (platformKey === 'bluesky') {
        setShowBlueskyModal(true);
      } else if (platformKey === 'farcaster') {
        // This case is now handled by the FarcasterConnectButton component
      }
    } catch (error) {
      console.error(`Failed to start ${platformKey} auth:`, error);
      alert(`Failed to connect to ${platformKey}.`);
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platformKey: string) => {
    if (!session?.user) return;
    setDisconnecting(platformKey);
    try {
      const { error } = await supabase
        .from('oauth_tokens')
        .delete()
        .eq('user_id', session.user.id)
        .eq('platform', platformKey);

      if (error) {
        throw error;
      }
      
      // Revert to intent on disconnect
      setPostingMethod(platformKey as keyof typeof platforms, 'intent');

      // Refresh connections to update the UI
      await checkPlatformConnections();

    } catch (error) {
      console.error(`Failed to disconnect ${platformKey}:`, error);
      alert(`Failed to disconnect ${platformKey}`);
    } finally {
      setDisconnecting(null);
    }
  };



  const handleExport = async () => {
    try {
      await exportDrafts()
    } catch (error) {
      console.error('Failed to export drafts:', error)
      alert('Failed to export drafts.')
    }
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const text = await file.text()
    try {
      const importedDrafts = JSON.parse(text)
      if (Array.isArray(importedDrafts)) {
        await importDrafts(importedDrafts)
        alert('Drafts imported!')
      } else {
        alert('Invalid drafts file.')
      }
    } catch (err) {
      alert('Invalid JSON file.')
    }
    e.target.value = ''
  }

  return (
    <div className="account-page-screen">
      <ViewHeader
        navLabel="Account navigation"
        actions={[
          {
            icon: 'close',
            label: 'Close account',
            onClick: () => navigate('/'),
          },
        ]}
      />
      <div className="account-page-scroll">
        <div className="account-page-layout lw-container">
          <div className="account-page-column">
            {accountNotice ? (
              <div className="account-notice" role="status">
                {accountNotice}
              </div>
            ) : null}
            <div className="account-page-content">
              <div className="account-page">
                <section className="account-section">
                  <h1>{hasAccount ? 'Account' : 'Create account'}</h1>
                  {session?.user ? (  
                    <div className="account-row">
                      <div className="account-row-meta">
                        <p className="user-email">{session.user.email}</p>
                        {isBetaUser && (
                          <span className="beta-badge" style={{ color: '#fff', background: 'var(--main-action-color)', borderRadius: 4, padding: '2px 6px', fontSize: 12 }}>
                            Beta
                          </span>
                        )}
                      </div>

                      <button onClick={signOut} className="sign-out-button">
                        Sign out
                      </button>
                    </div>
                  ) : (
                    <button onClick={signInWithGoogle} className="google-sign-in-button">
                      Sign in with Google
                    </button>
                  )}
                </section>

                <section className="account-section">
                  <h2>Appearance</h2>
                  <div className="theme-toggle-container">
                    <label className="theme-toggle-label">
                      <span>Theme</span>
                      <button
                        type="button"
                        className="theme-toggle-switch"
                        onClick={() => {
                          const currentTheme = settings?.theme || 'light';
                          const nextTheme: 'light' | 'dark' = currentTheme === 'light' ? 'dark' : 'light';
                          updateSettings({ theme: nextTheme });
                        }}
                        aria-label={`Switch to ${settings?.theme === 'light' ? 'dark' : 'light'} mode`}
                      >
                        <span className={`theme-toggle-slider ${settings?.theme === 'dark' ? 'dark' : 'light'}`}>
                          <span className="theme-toggle-icon">
                            {settings?.theme === 'dark' ? '🌑' : '☀️'}
                          </span>
                        </span>
                      </button>
                    </label>
                  </div>
                </section>

                {hasAccount ? (
                  <section className="account-section">
                    <h2>Feed</h2>
                    <p className="account-subtext">
                      Show posts that were hidden because one or more platform deletions failed.
                    </p>
                    <div className="theme-toggle-container">
                      <label className="theme-toggle-label">
                        <span>Show hidden posts</span>
                        <button
                          type="button"
                          className="theme-toggle-switch"
                          onClick={() => updateSettings({ showHiddenPosts: !(settings?.showHiddenPosts ?? false) })}
                          aria-label={`${settings?.showHiddenPosts ? 'Hide' : 'Show'} hidden posts`}
                        >
                          <span className={`theme-toggle-slider ${settings?.showHiddenPosts ? 'dark' : 'light'}`}>
                            <span className="theme-toggle-icon">
                              {settings?.showHiddenPosts ? 'H' : ''}
                            </span>
                          </span>
                        </button>
                      </label>
                    </div>
                  </section>
                ) : null}

                {hasOauthConnection ? (
                  <section className="account-section">
                    <h2>Clarity Examples</h2>
                    <p className="account-subtext">
                      Provide JSON to group examples by style. When styles are present, preview mode will show a toggle for each style.
                    </p>
                    <textarea
                      className="clarity-examples-input"
                      value={settings?.clarityExamples || ''}
                      onChange={(event) => updateSettings({ clarityExamples: event.target.value })}
                      placeholder={`{
  "playful": ["Keep it tight and witty.", "Short sentences. Light tone."],
  "direct": ["Clear, concise, no fluff."]
}`}
                      rows={8}
                    />
                  </section>
                ) : null}

                <section className="account-section">
                  <h2>Drafts</h2>
                  <div className="account-column">
                    <button onClick={handleExport} className="export-button">
                      Export Drafts
                    </button>
                    <button onClick={handleImportClick} className="import-button">
                      Import Drafts
                    </button>
                    <input
                      type="file"
                      accept="application/json"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                  </div>
                </section>

                <section className="account-section">
                  <h2>Post to Platforms</h2>
                  <div className="platform-list">
                    {Object.entries(platforms).map(([key, platform]) => (
                      <PlatformRow
                        key={key}
                        session={session}
                        platformKey={key}
                        platform={platform as any}
                        isEnabled={enabledPlatforms[key as keyof typeof enabledPlatforms]}
                        isConnected={platformConnections[key as keyof typeof platformConnections]}
                        postingMethod={postingMethods[key as keyof typeof postingMethods]}
                        isConnecting={connecting === key}
                        isDisconnecting={disconnecting === key}
                        onToggleEnabled={() => togglePlatform(key as keyof typeof enabledPlatforms)}
                        onConnect={() => handleConnect(key)}
                        onDisconnect={() => handleDisconnect(key)}
                        onSetPostingMethod={(method) => setPostingMethod(key as keyof typeof platforms, method)}
                        customConnectComponent={
                          key === 'farcaster' && !platformConnections.farcaster
                            ? <FarcasterConnectButton onSignInSuccess={handleFarcasterSignIn} />
                            : undefined
                        }
                      />
                    ))}
                  </div>
                  <div className="platform-explainer-row">
                    <span className="platform-explainer-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--main-action-color)' }}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="8" r="1"/><path d="M12 16v-4"/></svg>
                    </span>
                    <span className="platform-explainer-text">
                      <em style={{ fontSize: '14px' }}>
                        Tip: Add <b>#x</b>, <b>#t</b>, <b>#b</b>, or <b>#w</b> to your post to override and post only to the specified platform. E.g., <span style={{ fontFamily: 'monospace' }}>&quot;This skeet is for Bluesky only #b&quot;</span>
                      </em>
                    </span>
                  </div>
                </section>
              </div>
            </div>
            <div className="account-feedback">
              <button
                type="button"
                className="account-feedback-button"
                onClick={() => {
                  const tweetText = encodeURIComponent('@zherring, some litewrite feedback...');
                  const twitterIntentUrl = `https://twitter.com/intent/tweet?text=${tweetText}`;
                  window.open(twitterIntentUrl, '_blank');
                }}
              >
                send feedback
              </button>
            </div>
          </div>
        </div>
      </div>
      <BlueskyConnectModal
        isOpen={showBlueskyModal}
        userId={session?.user?.id || ''}
        accessToken={session?.access_token}
        onClose={() => {
          setShowBlueskyModal(false)
          setConnecting(null)
        }}
        onSuccess={() => {
          checkPlatformConnections()
          setPostingMethod('bluesky', 'oauth')
          setShowBlueskyModal(false)
        }}
      />
    </div>
  )
} 
